/**
 * Générateur — Faire un choix en filtrant les données d'un tableau selon un critère
 * Attendu ID : 678 | Niveau : 6ème | Domaine : Données et probabilités
 *
 * Types générés : qcm_unique, texte, relier, tableau_vf, texte_trou_drag
 * Template : filtrage_tableau_critere
 *
 * Labels de référence :
 *   - Lire un tableau et identifier la donnée correspondant à un critère
 *   - Filtrer les lignes d'un tableau selon une condition
 *   - Comparer des valeurs dans une colonne pour trouver un extremum
 *   - Associer critères et résultats de filtrage
 *
 * Variantes estimées : 300+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 678;
const GEN_KEY = 'filtrage_tableau_critere';

const NAMES_POOL = [
    'Léa', 'Hugo', 'Emma', 'Lucas', 'Chloé', 'Nathan', 'Jade', 'Louis',
    'Manon', 'Théo', 'Inès', 'Raphaël', 'Zoé', 'Adam', 'Lina', 'Gabriel',
    'Alice', 'Arthur', 'Clara', 'Victor',
];

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || 'Filtrer les données d\'un tableau selon un critère',
});

// ─── Helpers ─────────────────────────────────────────────────

const minutesToTime = (m) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h${String(mm).padStart(2, '0')}`;
};

const formatEuro = (v) => v.toFixed(2).replace('.', ',');

const buildTableHTML = (columns, rows) => {
    let html = '<table border="1" cellpadding="5" cellspacing="0">';
    html += '<tr>' + columns.map(c => `<th>${c}</th>`).join('') + '</tr>';
    for (const row of rows) {
        html += '<tr>' + row.map(v => `<td>${v}</td>`).join('') + '</tr>';
    }
    html += '</table>';
    return html;
};

// ─── Context generators ─────────────────────────────────────

/**
 * Each context generator returns:
 * {
 *   intro: string,
 *   columns: string[],
 *   rows: string[][],        // displayable values
 *   nameCol: number,         // index of the "name" column
 *   criteria: Array<{ label, colIndex, compare, filterFn? }>
 *     compare: 'min' | 'max' | 'filter'
 *     filterFn: (row, rowIndex) => boolean  (for filter type)
 * }
 */

const genContextCovoiturage = (nbRows) => {
    const drivers = ['Romain', 'Freddy', 'Isa', 'Séverine', 'Éric', 'Nadia', 'Jules'];
    const selectedDrivers = randomChoiceMultiple(drivers, nbRows);
    const departures = ['7h00', '7h30', '8h00', '8h30', '9h00'];

    const rows = [];
    const rawData = [];

    for (let i = 0; i < nbRows; i++) {
        const depStr = randomChoice(departures);
        const depMinutes = parseInt(depStr.split('h')[0]) * 60 + parseInt(depStr.split('h')[1]);
        const durationH = randInt(4, 6);
        const durationM = randInt(0, 5) * 10;
        const arrMinutes = depMinutes + durationH * 60 + durationM;
        const arrStr = minutesToTime(arrMinutes);
        const tarif = randInt(1800, 3000) / 100;

        rows.push([selectedDrivers[i], depStr, arrStr, formatEuro(tarif)]);
        rawData.push({ dep: depMinutes, arr: arrMinutes, tarif });
    }

    // Ensure unique tarifs for clean "cheapest" answers
    const tarifs = rawData.map(d => d.tarif);
    if (new Set(tarifs).size < tarifs.length) return genContextCovoiturage(nbRows);

    const criteria = [
        {
            label: 'le trajet le moins cher',
            question: 'Quel conducteur propose le trajet le moins cher ?',
            colIndex: 3,
            compare: 'min',
            valueOf: (i) => rawData[i].tarif,
        },
        {
            label: 'l\'arrivée la plus tôt',
            question: 'Quel conducteur arrive le plus tôt ?',
            colIndex: 2,
            compare: 'min',
            valueOf: (i) => rawData[i].arr,
        },
        {
            label: 'le départ le plus tard',
            question: 'Quel conducteur part le plus tard ?',
            colIndex: 1,
            compare: 'max',
            valueOf: (i) => rawData[i].dep,
        },
    ];

    return {
        intro: 'Voici les offres de covoiturage disponibles pour un trajet :',
        columns: ['Conducteur', 'Départ', 'Arrivée', 'Tarif (€)'],
        rows,
        nameCol: 0,
        criteria,
        rawData,
    };
};

const genContextCourse = (nbRows) => {
    const names = randomChoiceMultiple(NAMES_POOL, nbRows);
    const rows = [];
    const rawData = [];

    for (let i = 0; i < nbRows; i++) {
        const e1 = randInt(5, 15);
        const e2 = randInt(5, 15);
        const total = e1 + e2;
        rows.push([names[i], String(e1), String(e2), String(total)]);
        rawData.push({ e1, e2, total });
    }

    const totals = rawData.map(d => d.total);
    if (new Set(totals).size < totals.length) return genContextCourse(nbRows);

    const criteria = [
        {
            label: 'le coureur le plus rapide au total',
            question: 'Quel coureur a le temps total le plus court ?',
            colIndex: 3,
            compare: 'min',
            valueOf: (i) => rawData[i].total,
        },
        {
            label: 'le coureur le plus rapide à l\'étape 1',
            question: 'Quel coureur a été le plus rapide à l\'étape 1 ?',
            colIndex: 1,
            compare: 'min',
            valueOf: (i) => rawData[i].e1,
        },
        {
            label: 'le coureur le plus rapide à l\'étape 2',
            question: 'Quel coureur a été le plus rapide à l\'étape 2 ?',
            colIndex: 2,
            compare: 'min',
            valueOf: (i) => rawData[i].e2,
        },
    ];

    return {
        intro: 'Voici les résultats d\'une course à pied en deux étapes :',
        columns: ['Coureur', 'Étape 1 (min)', 'Étape 2 (min)', 'Total (min)'],
        rows,
        nameCol: 0,
        criteria,
        rawData,
    };
};

const genContextPostaux = () => {
    const types = ['Lettre verte 20g', 'Lettre verte 100g', 'Lettre verte 250g', 'Recommandé 20g', 'Recommandé 100g'];
    const masses = ['20 g', '100 g', '250 g', '20 g', '100 g'];
    const basePrices = [1.16, 2.32, 3.56, 4.30, 5.20];

    const rows = [];
    const rawData = [];

    for (let i = 0; i < types.length; i++) {
        const variation = randInt(-20, 20) / 100;
        const price = Math.round((basePrices[i] + variation) * 100) / 100;
        rows.push([types[i], masses[i], formatEuro(price)]);
        rawData.push({ type: types[i], mass: masses[i], price, maxMass: parseInt(masses[i]) });
    }

    const criteria = [
        {
            label: 'le tarif le moins cher pour une lettre de 80 g',
            question: 'Quel type d\'envoi est le moins cher pour envoyer une lettre de 80 g ?',
            colIndex: 2,
            compare: 'min',
            valueOf: (i) => rawData[i].maxMass >= 80 ? rawData[i].price : Infinity,
        },
        {
            label: 'le tarif du recommandé 100 g',
            question: 'Quel est le tarif du recommandé 100 g ?',
            colIndex: 2,
            compare: 'exact',
            exactRow: 4,
            valueOf: (i) => i === 4 ? 0 : Infinity,
        },
        {
            label: 'le tarif le moins cher pour une lettre de 15 g',
            question: 'Quel type d\'envoi est le moins cher pour envoyer une lettre de 15 g ?',
            colIndex: 2,
            compare: 'min',
            valueOf: (i) => rawData[i].maxMass >= 15 ? rawData[i].price : Infinity,
        },
    ];

    return {
        intro: 'Voici les tarifs postaux pour différents types d\'envois :',
        columns: ['Type d\'envoi', 'Masse max', 'Tarif (€)'],
        rows,
        nameCol: 0,
        criteria,
        rawData,
    };
};

const genContextTrains = (nbRows) => {
    const trainNames = ['TER 1', 'TER 2', 'TER 3', 'TER 4', 'TER 5', 'TER 6'];
    const selected = trainNames.slice(0, nbRows);
    const rows = [];
    const rawData = [];

    for (let i = 0; i < nbRows; i++) {
        const depMinutes = randInt(6 * 60, 14 * 60);
        const duration = randInt(40, 180);
        const arrMinutes = depMinutes + duration;
        rows.push([selected[i], minutesToTime(depMinutes), minutesToTime(arrMinutes), `${duration} min`]);
        rawData.push({ dep: depMinutes, arr: arrMinutes, duration });
    }

    const durations = rawData.map(d => d.duration);
    if (new Set(durations).size < durations.length) return genContextTrains(nbRows);

    const criteria = [
        {
            label: 'l\'arrivée la plus tôt',
            question: 'Quel train arrive le plus tôt ?',
            colIndex: 2,
            compare: 'min',
            valueOf: (i) => rawData[i].arr,
        },
        {
            label: 'le trajet le plus court',
            question: 'Quel train a le trajet le plus court ?',
            colIndex: 3,
            compare: 'min',
            valueOf: (i) => rawData[i].duration,
        },
        {
            label: 'le départ le plus tard',
            question: 'Quel train part le plus tard ?',
            colIndex: 1,
            compare: 'max',
            valueOf: (i) => rawData[i].dep,
        },
        {
            label: 'le départ après 10h le plus tôt',
            question: 'Quel est le premier train qui part après 10h ?',
            colIndex: 1,
            compare: 'min',
            valueOf: (i) => rawData[i].dep >= 600 ? rawData[i].dep : Infinity,
        },
    ];

    return {
        intro: 'Voici les horaires de trains disponibles :',
        columns: ['Train', 'Départ', 'Arrivée', 'Durée'],
        rows,
        nameCol: 0,
        criteria,
        rawData,
    };
};

const genContextMeteo = (nbRows) => {
    const villes = ['Paris', 'Lyon', 'Marseille', 'Bordeaux', 'Toulouse', 'Lille', 'Strasbourg'];
    const selected = randomChoiceMultiple(villes, nbRows);
    const rows = [];
    const rawData = [];

    for (let i = 0; i < nbRows; i++) {
        const temp = randInt(10, 28);
        const pluie = randInt(30, 100);
        const jours = randInt(8, 20);
        rows.push([selected[i], String(temp), String(pluie), String(jours)]);
        rawData.push({ temp, pluie, jours });
    }

    const temps = rawData.map(d => d.temp);
    if (new Set(temps).size < temps.length) return genContextMeteo(nbRows);

    const criteria = [
        {
            label: 'la ville la plus chaude',
            question: 'Quelle est la ville la plus chaude ?',
            colIndex: 1,
            compare: 'max',
            valueOf: (i) => rawData[i].temp,
        },
        {
            label: 'la ville avec le moins de pluie',
            question: 'Quelle ville a reçu le moins de pluie ?',
            colIndex: 2,
            compare: 'min',
            valueOf: (i) => rawData[i].pluie,
        },
        {
            label: 'la ville la plus froide',
            question: 'Quelle est la ville la plus froide ?',
            colIndex: 1,
            compare: 'min',
            valueOf: (i) => rawData[i].temp,
        },
        {
            label: 'la ville avec le plus de jours de pluie',
            question: 'Quelle ville a le plus de jours de pluie ?',
            colIndex: 3,
            compare: 'max',
            valueOf: (i) => rawData[i].jours,
        },
    ];

    return {
        intro: 'Voici les données météo du mois dernier pour plusieurs villes :',
        columns: ['Ville', 'Température (°C)', 'Pluie (mm)', 'Jours de pluie'],
        rows,
        nameCol: 0,
        criteria,
        rawData,
    };
};

// ─── Pick a random context ──────────────────────────────────

const pickContext = (nbRows) => {
    if (!nbRows) nbRows = randInt(4, 6);
    const gen = randomChoice([
        () => genContextCovoiturage(nbRows),
        () => genContextCourse(nbRows),
        () => genContextPostaux(),
        () => genContextTrains(nbRows),
        () => genContextMeteo(nbRows),
    ]);
    return gen();
};

/** Find winner index for a given criterion */
const findWinner = (ctx, criterion) => {
    let bestIdx = 0;
    let bestVal = criterion.valueOf(0);
    for (let i = 1; i < ctx.rows.length; i++) {
        const v = criterion.valueOf(i);
        if (criterion.compare === 'min' && v < bestVal) {
            bestIdx = i;
            bestVal = v;
        } else if (criterion.compare === 'max' && v > bestVal) {
            bestIdx = i;
            bestVal = v;
        }
    }
    if (bestVal === Infinity || bestVal === -Infinity) return -1;
    return bestIdx;
};

/**
 * Gagnant strictement unique ? (revue 2026-08-08) En cas d'ex æquo au sommet,
 * findRunnerUp renvoie le co-gagnant — servi en piège et compté faux alors
 * qu'il est tout aussi juste (350 tirages sur 6000 au QCM).
 */
const winnerIsUnique = (ctx, criterion) => {
    const winnerIdx = findWinner(ctx, criterion);
    if (winnerIdx < 0) return false;
    const bestVal = criterion.valueOf(winnerIdx);
    let nb = 0;
    for (let i = 0; i < ctx.rows.length; i++) {
        if (criterion.valueOf(i) === bestVal) nb++;
    }
    return nb === 1;
};

const findRunnerUp = (ctx, criterion) => {
    const winnerIdx = findWinner(ctx, criterion);
    let bestIdx = -1;
    let bestVal = criterion.compare === 'min' ? Infinity : -Infinity;
    for (let i = 0; i < ctx.rows.length; i++) {
        if (i === winnerIdx) continue;
        const v = criterion.valueOf(i);
        if (v === Infinity || v === -Infinity) continue;
        if (criterion.compare === 'min' && v < bestVal) {
            bestIdx = i;
            bestVal = v;
        } else if (criterion.compare === 'max' && v > bestVal) {
            bestIdx = i;
            bestVal = v;
        }
    }
    return bestIdx;
};

// ─── QCM ─────────────────────────────────────────────────────

const genQcm = () => {
    const ctx = pickContext();
    const tableHTML = buildTableHTML(ctx.columns, ctx.rows);
    const subVariant = randomChoice(['find_name', 'find_name', 'find_value']);

    if (subVariant === 'find_name') {
        const criterion = randomChoice(ctx.criteria);
        const winnerIdx = findWinner(ctx, criterion);
        if (winnerIdx < 0) return genQcm();
        // Gagnant unique exigé (revue 2026-08-08) : sinon le co-gagnant est
        // dans les pièges et compté faux.
        if (!winnerIsUnique(ctx, criterion)) return genQcm();

        const winnerName = ctx.rows[winnerIdx][ctx.nameCol];
        const otherNames = ctx.rows
            .filter((_, i) => i !== winnerIdx)
            .map(r => r[ctx.nameCol]);
        const pieges = shuffle(otherNames).slice(0, 3);

        // Ensure runner-up is among pièges
        const runnerUpIdx = findRunnerUp(ctx, criterion);
        if (runnerUpIdx >= 0) {
            const runnerUpName = ctx.rows[runnerUpIdx][ctx.nameCol];
            if (!pieges.includes(runnerUpName)) {
                pieges[0] = runnerUpName;
            }
        }

        const texte = `<p>${ctx.intro}</p>${tableHTML}<p>${criterion.question}</p>`;
        const allChoix = [winnerName, ...pieges];

        const payload = makeQcm(allChoix, winnerName, texte, opts());
        payload._meta.generatorKey = GEN_KEY;
        return payload;
    } else {
        // find_value: "Quelle est la valeur de [col] pour [nom] ?"
        const rowIdx = randInt(0, ctx.rows.length - 1);
        const valueCols = [];
        for (let c = 0; c < ctx.columns.length; c++) {
            if (c !== ctx.nameCol) valueCols.push(c);
        }
        const colIdx = randomChoice(valueCols);
        const name = ctx.rows[rowIdx][ctx.nameCol];
        const correctVal = ctx.rows[rowIdx][colIdx];

        const wrongVals = ctx.rows
            .filter((_, i) => i !== rowIdx)
            .map(r => r[colIdx])
            .filter(v => v !== correctVal);
        const pieges = shuffle([...new Set(wrongVals)]).slice(0, 3);
        if (pieges.length < 3) return genQcm();

        const texte = `<p>${ctx.intro}</p>${tableHTML}<p>Quelle est la valeur de « ${ctx.columns[colIdx]} » pour ${name} ?</p>`;
        const allChoix = [correctVal, ...pieges];

        const payload = makeQcm(allChoix, correctVal, texte, opts());
        payload._meta.generatorKey = GEN_KEY;
        return payload;
    }
};

// ─── Saisie (texte) ──────────────────────────────────────────

const genSaisie = () => {
    const ctx = pickContext();
    const tableHTML = buildTableHTML(ctx.columns, ctx.rows);
    const subVariant = randomChoice(['criterion', 'value']);

    if (subVariant === 'criterion') {
        const criterion = randomChoice(ctx.criteria);
        const winnerIdx = findWinner(ctx, criterion);
        if (winnerIdx < 0) return genSaisie();
        // Gagnant unique exigé (revue 2026-08-08) : l'élève qui tape le
        // co-gagnant d'un ex æquo était compté faux (7,9 % des tirages).
        if (!winnerIsUnique(ctx, criterion)) return genSaisie();

        const winnerName = ctx.rows[winnerIdx][ctx.nameCol];
        const texte = `<p>${ctx.intro}</p>${tableHTML}<p>D'après le tableau, ${criterion.label} correspond à quel nom ? (Écris le nom uniquement.)</p>`;

        const payload = makeSaisie(winnerName, texte, opts());
        payload._meta.generatorKey = GEN_KEY;
        return payload;
    } else {
        const rowIdx = randInt(0, ctx.rows.length - 1);
        const valueCols = [];
        for (let c = 0; c < ctx.columns.length; c++) {
            if (c !== ctx.nameCol) valueCols.push(c);
        }
        const colIdx = randomChoice(valueCols);
        const name = ctx.rows[rowIdx][ctx.nameCol];
        const correctVal = ctx.rows[rowIdx][colIdx];

        const texte = `<p>${ctx.intro}</p>${tableHTML}<p>Quelle est la valeur de « ${ctx.columns[colIdx]} » pour ${name} ?</p>`;

        // Unité en suffixe (revue 2026-08-08, mécanique du gen 676) : pour
        // « 104 min » ou « 100 g », l'élève qui tapait « 104 » était compté
        // faux — l'attendu devient le nombre seul, l'unité s'affiche après le
        // champ. Heures (« 10h48 ») et euros (« 25,40 ») inchangés.
        const mUnite = correctVal.match(/^(\d+(?:,\d+)?) (min|g)$/);
        const payload = mUnite
            ? makeSaisie(mUnite[1], texte, { ...opts(), suffixe: mUnite[2] })
            : makeSaisie(correctVal, texte, opts());
        payload._meta.generatorKey = GEN_KEY;
        return payload;
    }
};

// ─── Relier ──────────────────────────────────────────────────

const genRelier = () => {
    // Need at least 4 criteria with different winners
    // Use contexts that have enough criteria; may need to retry
    let ctx;
    let validPairs;
    let attempts = 0;

    while (attempts < 20) {
        attempts++;
        ctx = pickContext(randInt(5, 6));

        const pairsMap = new Map();
        for (const criterion of ctx.criteria) {
            const winnerIdx = findWinner(ctx, criterion);
            if (winnerIdx < 0) continue;
            // Gagnant unique exigé (revue 2026-08-08) : 839 paires sur critère
            // à ex æquo — le co-gagnant relié était compté faux.
            if (!winnerIsUnique(ctx, criterion)) continue;
            const winnerName = ctx.rows[winnerIdx][ctx.nameCol];
            // Only keep if this winner is unique among pairs
            const usedWinners = [...pairsMap.values()];
            if (!usedWinners.includes(winnerName)) {
                pairsMap.set(criterion.label, winnerName);
            }
        }

        // If not enough from single context, augment with value lookups
        if (pairsMap.size < 4) {
            for (let i = 0; i < ctx.rows.length && pairsMap.size < 4; i++) {
                const name = ctx.rows[i][ctx.nameCol];
                const usedWinners = [...pairsMap.values()];
                if (usedWinners.includes(name)) continue;

                const valueCols = [];
                for (let c = 0; c < ctx.columns.length; c++) {
                    if (c !== ctx.nameCol) valueCols.push(c);
                }
                const colIdx = randomChoice(valueCols);
                const val = ctx.rows[i][colIdx];
                // Valeur unique dans sa colonne exigée (revue 2026-08-08) :
                // « Masse max = 20 g » désignait DEUX lignes du tableau postal,
                // l'autre ligne reliée était comptée fausse (1462 paires/5000).
                if (ctx.rows.filter(r => r[colIdx] === val).length > 1) continue;
                const label = `${ctx.columns[colIdx]} = ${val}`;
                if (!pairsMap.has(label)) {
                    pairsMap.set(label, name);
                }
            }
        }

        if (pairsMap.size >= 4) {
            validPairs = [...pairsMap.entries()].slice(0, 4).map(([k, v]) => ({
                source: k.charAt(0).toUpperCase() + k.slice(1),
                target: v,
            }));
            break;
        }
    }

    if (!validPairs || validPairs.length < 4) return genRelier();

    const tableHTML = buildTableHTML(ctx.columns, ctx.rows);
    const texte = `<p>${ctx.intro}</p>${tableHTML}<p>Relie chaque critère au nom correspondant dans le tableau.</p>`;

    const payload = makeRelier(validPairs, texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── Vrai / Faux ────────────────────────────────────────────

const genVF = () => {
    const ctx = pickContext(randInt(4, 6));
    const tableHTML = buildTableHTML(ctx.columns, ctx.rows);

    const vraisPool = [];
    const fauxPool = [];

    // Generate V/F statements from criteria.
    // Garde d'unicité (revue 2026-08-08) : en cas d'ex æquo, findRunnerUp
    // renvoie le co-gagnant — « … est {co-gagnant} » étiquetée Faux était
    // VRAIE (335 lignes sur 25 000). Clés sémantiques : cf. sélection.
    for (const criterion of ctx.criteria) {
        const winnerIdx = findWinner(ctx, criterion);
        if (winnerIdx < 0) continue;
        if (!winnerIsUnique(ctx, criterion)) continue;
        const winnerName = ctx.rows[winnerIdx][ctx.nameCol];
        const runnerUpIdx = findRunnerUp(ctx, criterion);
        const kCrit = `crit:${criterion.label}`;

        // VRAI: correct winner
        vraisPool.push({ l: `${criterion.label.charAt(0).toUpperCase() + criterion.label.slice(1)} est ${winnerName}.`, k: kCrit });

        // FAUX: wrong winner (strictement moins bon, l'unicité est garantie)
        if (runnerUpIdx >= 0) {
            const wrongName = ctx.rows[runnerUpIdx][ctx.nameCol];
            fauxPool.push({ l: `${criterion.label.charAt(0).toUpperCase() + criterion.label.slice(1)} est ${wrongName}.`, k: kCrit });
        }
    }

    // Generate comparison statements
    const valueCols = [];
    for (let c = 0; c < ctx.columns.length; c++) {
        if (c !== ctx.nameCol) valueCols.push(c);
    }

    if (valueCols.length > 0 && ctx.rows.length >= 2) {
        for (let attempt = 0; attempt < 6; attempt++) {
            const col = randomChoice(valueCols);
            const [i, j] = randomChoiceMultiple([...Array(ctx.rows.length).keys()], 2);
            const nameA = ctx.rows[i][ctx.nameCol];
            const nameB = ctx.rows[j][ctx.nameCol];
            const valA = ctx.rows[i][col];
            const valB = ctx.rows[j][col];

            // Try to parse as numbers for comparison
            const numA = parseFloat(String(valA).replace(',', '.'));
            const numB = parseFloat(String(valB).replace(',', '.'));

            if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
                const colLabel = ctx.columns[col];
                const kComp = `comp:${[nameA, nameB].sort().join('|')}|${colLabel}`;
                if (numA < numB) {
                    vraisPool.push({ l: `${nameA} a une valeur de ${colLabel} plus petite que ${nameB}.`, k: kComp });
                    fauxPool.push({ l: `${nameB} a une valeur de ${colLabel} plus petite que ${nameA}.`, k: kComp });
                } else {
                    vraisPool.push({ l: `${nameB} a une valeur de ${colLabel} plus petite que ${nameA}.`, k: kComp });
                    fauxPool.push({ l: `${nameA} a une valeur de ${colLabel} plus petite que ${nameB}.`, k: kComp });
                }
            }
        }
    }

    // Also add direct value statements
    if (ctx.rows.length >= 2 && valueCols.length > 0) {
        const rowIdx = randInt(0, ctx.rows.length - 1);
        const col = randomChoice(valueCols);
        const name = ctx.rows[rowIdx][ctx.nameCol];
        const val = ctx.rows[rowIdx][col];
        const kVal = `val:${name}|${ctx.columns[col]}`;
        vraisPool.push({ l: `La valeur de « ${ctx.columns[col]} » pour ${name} est ${val}.`, k: kVal });

        // False: use a value from another row
        const otherIdx = (rowIdx + 1) % ctx.rows.length;
        const wrongVal = ctx.rows[otherIdx][col];
        if (wrongVal !== val) {
            fauxPool.push({ l: `La valeur de « ${ctx.columns[col]} » pour ${name} est ${wrongVal}.`, k: kVal });
        }
    }

    if (vraisPool.length < 2 || fauxPool.length < 2) return genVF();

    const nbVrais = randInt(2, Math.min(3, vraisPool.length));
    const nbFaux = 5 - nbVrais;

    if (fauxPool.length < nbFaux) return genVF();

    // Sélection à clés sémantiques toutes distinctes (revue 2026-08-08) :
    // 58,6 % des tirages avaient deux affirmations sur le même objet.
    const seenKeys = new Set();
    const pick = (pool, n) => {
        const out = [];
        for (const e of shuffle(pool)) {
            if (out.length >= n) break;
            if (!seenKeys.has(e.k)) {
                seenKeys.add(e.k);
                out.push(e.l);
            }
        }
        return out;
    };
    const selectedVrais = pick(vraisPool, nbVrais);
    const selectedFaux = pick(fauxPool, nbFaux);
    if (selectedVrais.length < nbVrais || selectedFaux.length < nbFaux) return genVF();

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);

    const lignes = mixed.map(m => m.l);
    const bonnesReponses = mixed.map(m => m.r);

    const texte = `<p>${ctx.intro}</p>${tableHTML}<p>Indique si chaque affirmation est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts());
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── Texte à trous (drag) ───────────────────────────────────

const genTrouDrag = () => {
    const ctx = pickContext(randInt(4, 6));
    const tableHTML = buildTableHTML(ctx.columns, ctx.rows);
    const modele = randomChoice(['M1', 'M2']);

    let phrase, distracteurs;

    if (modele === 'M1') {
        // "Pour trouver [critère], on compare la colonne {{Col}}. La valeur la plus [basse/haute] est {{val}}, c'est {{Nom}}."
        const criterion = randomChoice(ctx.criteria);
        const winnerIdx = findWinner(ctx, criterion);
        if (winnerIdx < 0) return genTrouDrag();
        // Gagnant unique exigé (revue 2026-08-08) : le co-gagnant d'un ex æquo
        // était en étiquette et compté faux (306 tirages sur 6000, M1 + M2).
        if (!winnerIsUnique(ctx, criterion)) return genTrouDrag();

        const winnerName = ctx.rows[winnerIdx][ctx.nameCol];
        const colName = ctx.columns[criterion.colIndex];
        const winnerVal = ctx.rows[winnerIdx][criterion.colIndex];
        const comparWord = criterion.compare === 'min' ? 'basse' : 'haute';

        phrase = `Pour trouver ${criterion.label}, on compare la colonne {{${colName}}}. La valeur la plus ${comparWord} est {{${winnerVal}}}, c'est {{${winnerName}}}.`;

        // Distracteurs: other column names, other values, other names
        const otherCols = ctx.columns.filter(c => c !== colName && c !== ctx.columns[ctx.nameCol]);
        const otherNames = ctx.rows.filter((_, i) => i !== winnerIdx).map(r => r[ctx.nameCol]);
        const otherVals = ctx.rows.filter((_, i) => i !== winnerIdx).map(r => r[criterion.colIndex]);

        distracteurs = [
            ...shuffle(otherCols).slice(0, 1),
            ...shuffle(otherVals).slice(0, 1),
            ...shuffle(otherNames).slice(0, 1),
        ];
    } else {
        // "[Nom1] propose {{val1}} et [Nom2] propose {{val2}}. Le [critère] est {{Nom correct}}."
        const criterion = randomChoice(ctx.criteria);
        const winnerIdx = findWinner(ctx, criterion);
        if (winnerIdx < 0) return genTrouDrag();
        // Même garde d'unicité que M1 (revue 2026-08-08).
        if (!winnerIsUnique(ctx, criterion)) return genTrouDrag();
        const runnerUpIdx = findRunnerUp(ctx, criterion);
        if (runnerUpIdx < 0) return genTrouDrag();

        const winnerName = ctx.rows[winnerIdx][ctx.nameCol];
        const runnerUpName = ctx.rows[runnerUpIdx][ctx.nameCol];
        const winnerVal = ctx.rows[winnerIdx][criterion.colIndex];
        const runnerUpVal = ctx.rows[runnerUpIdx][criterion.colIndex];

        phrase = `${winnerName} propose {{${winnerVal}}} et ${runnerUpName} propose {{${runnerUpVal}}}. ${criterion.label.charAt(0).toUpperCase() + criterion.label.slice(1)} est {{${winnerName}}}.`;

        // Distracteurs
        const otherNames = ctx.rows
            .filter((_, i) => i !== winnerIdx && i !== runnerUpIdx)
            .map(r => r[ctx.nameCol]);
        const otherVals = ctx.rows
            .filter((_, i) => i !== winnerIdx && i !== runnerUpIdx)
            .map(r => r[criterion.colIndex]);

        distracteurs = [
            runnerUpName,
            ...shuffle(otherVals).slice(0, 1),
            ...shuffle(otherNames).slice(0, 1),
        ];
    }

    const texte = `<p>${ctx.intro}</p>${tableHTML}<p>Complète les phrases en glissant les étiquettes proposées.</p>`;
    // Filtre contre les mots attendus : une autre ligne peut porter la même
    // valeur que la gagnante (ex. deux temps identiques "12h40")
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(),
        mode: 'drag',
        distracteurs: filtrerDistracteurs(phrase, distracteurs),
    });
    payload._meta.generatorKey = GEN_KEY;
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────
const GENERATORS = {
    filtrage_tableau_critere_qcm_unique: genQcm,
    filtrage_tableau_critere_texte: genSaisie,
    filtrage_tableau_critere_relier: genRelier,
    filtrage_tableau_critere_tableau_vf: genVF,
    filtrage_tableau_critere_texte_trou_drag: genTrouDrag,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'filtrage_tableau_critere',
        label: 'Filtrage de données dans un tableau selon un critère',
        formatsList: buildFormats('filtrage_tableau_critere'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du FORMAT résolu, TOUJOURS (pas celle de la carte posée par les
    // sous-générateurs) : c est elle que la carte d exerciseur compare pour
    // ses compteurs et que la réutilisation range (bug des compteurs muets
    // avant tout clic sur un onglet, tranché 2026-09-02).
    payload._meta.generatorKey = key;
    return payload;
};
